import { useState, useEffect, useCallback } from 'react'
import { cacheService } from '../services/cacheService'

export interface OfflineState {
  isOnline: boolean
  isOffline: boolean
  cacheStats: {
    totalSounds: number
    totalSize: number
    lastUpdated: number
  }
  isCacheInitialized: boolean
}

export const useOffline = () => {
  const [isOnline, setIsOnline] = useState(navigator.onLine)
  const [isCacheInitialized, setIsCacheInitialized] = useState(false)
  const [cacheStats, setCacheStats] = useState({
    totalSounds: 0,
    totalSize: 0,
    lastUpdated: 0
  })

  // Initialize cache service
  useEffect(() => {
    const initCache = async () => {
      try {
        await cacheService.init()
        setIsCacheInitialized(true)
        await updateCacheStats()
        console.log('Cache service initialized')
      } catch (error) {
        console.error('Failed to initialize cache service:', error)
      }
    }

    initCache()
  }, [])

  // Listen for online/offline events
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true)
      console.log('App is online')
    }

    const handleOffline = () => {
      setIsOnline(false)
      console.log('App is offline')
    }

    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)

    return () => {
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
    }
  }, [])

  // Update cache statistics
  const updateCacheStats = useCallback(async () => {
    if (!isCacheInitialized) return

    try {
      const stats = await cacheService.getCacheStats()
      setCacheStats(stats)
    } catch (error) {
      console.error('Failed to update cache stats:', error)
    }
  }, [isCacheInitialized])

  // Cache a sound
  const cacheSound = useCallback(async (sound: any, audioData: ArrayBuffer) => {
    if (!isCacheInitialized) {
      throw new Error('Cache service not initialized')
    }

    try {
      await cacheService.cacheSound(sound, audioData)
      await updateCacheStats()
      console.log(`Sound cached: ${sound.label}`)
    } catch (error) {
      console.error(`Failed to cache sound: ${sound.label}`, error)
      throw error
    }
  }, [isCacheInitialized, updateCacheStats])

  // Get cached sound
  const getCachedSound = useCallback(async (soundId: string) => {
    if (!isCacheInitialized) {
      return null
    }

    try {
      return await cacheService.getCachedSound(soundId)
    } catch (error) {
      console.error(`Failed to get cached sound: ${soundId}`, error)
      return null
    }
  }, [isCacheInitialized])

  // Check if sound is cached
  const isSoundCached = useCallback(async (soundId: string): Promise<boolean> => {
    if (!isCacheInitialized) {
      return false
    }

    try {
      return await cacheService.isSoundCached(soundId)
    } catch (error) {
      console.error(`Failed to check if sound is cached: ${soundId}`, error)
      return false
    }
  }, [isCacheInitialized])

  // Get cached sound as blob
  const getCachedSoundBlob = useCallback(async (soundId: string): Promise<Blob | null> => {
    if (!isCacheInitialized) {
      return null
    }

    try {
      return await cacheService.getCachedSoundBlob(soundId)
    } catch (error) {
      console.error(`Failed to get cached sound blob: ${soundId}`, error)
      return null
    }
  }, [isCacheInitialized])

  // Clear cache
  const clearCache = useCallback(async () => {
    if (!isCacheInitialized) {
      throw new Error('Cache service not initialized')
    }

    try {
      await cacheService.clearCache()
      await updateCacheStats()
      console.log('Cache cleared')
    } catch (error) {
      console.error('Failed to clear cache:', error)
      throw error
    }
  }, [isCacheInitialized, updateCacheStats])

  // Remove specific cached sound
  const removeCachedSound = useCallback(async (soundId: string) => {
    if (!isCacheInitialized) {
      throw new Error('Cache service not initialized')
    }

    try {
      await cacheService.removeCachedSound(soundId)
      await updateCacheStats()
      console.log(`Removed cached sound: ${soundId}`)
    } catch (error) {
      console.error(`Failed to remove cached sound: ${soundId}`, error)
      throw error
    }
  }, [isCacheInitialized, updateCacheStats])

  // Format cache size
  const formatCacheSize = useCallback((bytes: number): string => {
    return cacheService.formatCacheSize(bytes)
  }, [])

  return {
    isOnline,
    isOffline: !isOnline,
    cacheStats,
    isCacheInitialized,
    updateCacheStats,
    cacheSound,
    getCachedSound,
    isSoundCached,
    getCachedSoundBlob,
    clearCache,
    removeCachedSound,
    formatCacheSize
  }
}
